import React, { useState } from "react";
import { usePageTitle } from "../hooks/usePageTitle";
import { Link } from "react-router-dom";

const HowToPlay = () => {
  usePageTitle("How to Play");
  const [openSection, setOpenSection] = useState("getting-started");

  const toggleSection = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const Section = ({ id, title, children }) => (
    <div className="bg-dark-card border border-gray-700 rounded-lg mb-4">
      <button
        onClick={() => toggleSection(id)}
        className="w-full text-left px-6 py-4 flex justify-between items-center hover:bg-gray-800/50 transition"
      >
        <h2 className="text-2xl font-semibold text-white">{title}</h2>
        <span className="text-gray-400 text-2xl">
          {openSection === id ? "−" : "+"}
        </span>
      </button>
      {openSection === id && (
        <div className="px-6 pb-6 text-gray-300 space-y-4">{children}</div>
      )}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <h1 className="text-4xl font-bold text-white mb-6">How to Play</h1>
      <p className="text-gray-400 mb-8">
        A comprehensive guide to mastering Collect Arena
      </p>

      <div className="space-y-4">
        <Section id="getting-started" title="Getting Started">
          <div>
            <h3 className="text-xl font-semibold text-white mb-3">
              Step 1: Create an Account
            </h3>
            <p className="mb-3">
              <strong className="text-blue-400">Account required:</strong> You
              must create a free account to play. Guest access is not available.
            </p>
            <p>
              Click "Sign Up" in the top right corner and create your account to
              start playing!
            </p>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-white mb-3">
              Step 2: Browse Lists
            </h3>
            <p className="mb-3">
              After logging in, explore hundreds of curated lists across various
              categories:
            </p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Sports (Olympic sports, football leagues, etc.)</li>
              <li>Geography (countries, capitals, landmarks)</li>
              <li>Entertainment (movies, music, TV shows)</li>
              <li>Science (elements, planets, inventions)</li>
              <li>And many more!</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-white mb-3">
              Step 3: Select a List
            </h3>
            <p>Click on any list card to view its details. You'll see:</p>
            <ul className="list-disc list-inside space-y-1 ml-4">
              <li>Title and description</li>
              <li>Number of items to guess</li>
              <li>Categories and tags</li>
              <li>Your current progress (if any)</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-white mb-3">
              Step 4: Choose Your Mode
            </h3>
            <p className="mb-3">
              Before you start guessing, select your preferred modes:
            </p>
            <div className="bg-dark-bg border border-gray-600 rounded p-4">
              <p className="text-white font-semibold mb-2">Difficulty Mode:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>
                  <strong className="text-blue-400">Casual:</strong> Requires
                  exact spelling (ignores accents and case)
                </li>
                <li>
                  <strong className="text-yellow-400">Relaxed:</strong>{" "}
                  Tolerates typos with 90% similarity threshold
                </li>
              </ul>
              <p className="text-white font-semibold mt-4 mb-2">Game Mode:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>
                  <strong className="text-green-400">Normal:</strong> No time
                  limit, focus on accuracy
                </li>
                <li>
                  <strong className="text-red-400">Timed:</strong> Race against
                  the clock for speed
                </li>
              </ul>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-white mb-3">
              Step 5: Start Guessing!
            </h3>
            <p>
              Type your answers into the text box and press Enter or click
              "Submit Guess". Correct answers will reveal the hidden items!
            </p>
          </div>
        </Section>

        <Section id="difficulty-modes" title="Difficulty Modes Explained">
          <div>
            <h3 className="text-xl font-semibold text-blue-400 mb-3">
              Casual Mode
            </h3>
            <p className="mb-3">
              <strong>Perfect for:</strong> Precision-focused players who know
              their stuff
            </p>
            <p className="mb-3">
              <strong>How it works:</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                Requires <strong>exact spelling</strong> (case-insensitive)
              </li>
              <li>
                Ignores accents: "Beyonce" matches "Beyoncé", "Zurich" matches
                "Zürich"
              </li>
              <li>Must match the complete answer</li>
              <li>No tolerance for typos</li>
            </ul>
            <div className="bg-blue-500/10 border border-blue-500 rounded p-3 mt-3">
              <p className="text-blue-400">
                <strong>Example:</strong> For "Swimming"
              </p>
              <ul className="list-disc list-inside ml-4 mt-2">
                <li className="text-green-400">✓ "swimming" (works)</li>
                <li className="text-green-400">✓ "SWIMMING" (works)</li>
                <li className="text-red-400">✗ "Swiming" (doesn't work)</li>
                <li className="text-red-400">✗ "Swim" (doesn't work)</li>
              </ul>
            </div>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-yellow-400 mb-3">
              Relaxed Mode
            </h3>
            <p className="mb-3">
              <strong>Perfect for:</strong> Players who want a forgiving,
              stress-free experience
            </p>
            <p className="mb-3">
              <strong>How it works:</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                Uses <strong>fuzzy matching</strong> with Levenshtein distance
                algorithm
              </li>
              <li>Accepts answers with 90% similarity</li>
              <li>Tolerates minor typos and spelling mistakes</li>
              <li>Still case-insensitive and accent-insensitive</li>
            </ul>
            <div className="bg-yellow-500/10 border border-yellow-500 rounded p-3 mt-3">
              <p className="text-yellow-400">
                <strong>Example:</strong> For "Swimming"
              </p>
              <ul className="list-disc list-inside ml-4 mt-2">
                <li className="text-green-400">✓ "swimming" (works)</li>
                <li className="text-green-400">✓ "Swiming" (works - typo)</li>
                <li className="text-green-400">
                  ✓ "Swimmin" (works - missing letter)
                </li>
                <li className="text-red-400">
                  ✗ "Swim" (too short, below 90%)
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-purple-500/10 border border-purple-500 rounded-lg p-4">
            <p className="text-purple-400">
              <strong>Pro Tip:</strong> Try Casual mode first to challenge
              yourself, then switch to Relaxed if you're struggling with
              spelling!
            </p>
          </div>
        </Section>

        <Section id="game-modes" title="Game Modes Explained">
          <div>
            <h3 className="text-xl font-semibold text-green-400 mb-3">
              Normal Mode
            </h3>
            <p className="mb-3">
              <strong>Perfect for:</strong> Learning, exploring, and casual play
            </p>
            <p className="mb-3">
              <strong>Features:</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>No time pressure - take as long as you need</li>
              <li>
                Tracks <strong>total guesses</strong> (all attempts, not just
                correct ones)
              </li>
              <li>Saves your progress automatically</li>
              <li>Resume anytime from where you left off</li>
              <li>Records "Best (Normal)" and "Last (Normal)" guess counts</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-red-400 mb-3">
              Timed Mode
            </h3>
            <p className="mb-3">
              <strong>Perfect for:</strong> Speed challenges and competitive
              play
            </p>
            <p className="mb-3">
              <strong>Features:</strong>
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                Timer starts <strong>once you select Timed mode</strong>
              </li>
              <li>
                Timer <strong>pauses and remembers your time</strong> when you
                leave the page or close the tab
              </li>
              <li>
                Timer <strong>resumes</strong> from where you left off when you
                return
              </li>
              <li>
                Automatically stops when you complete the list and saves your
                time
              </li>
              <li>
                Records "Best Time", "Last Time", "Best (Timed)", and "Last
                (Timed)" stats
              </li>
            </ul>
          </div>

          <div className="bg-red-500/10 border border-red-500 rounded-lg p-4">
            <h4 className="text-red-400 font-semibold mb-2">
              Important: Timed Mode Rules
            </h4>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Starting Timed Mode:</strong> If you have existing
                progress in Normal mode, switching to Timed will reset ALL your
                guessed items
              </li>
              <li>
                <strong>Resetting Progress:</strong> Clicking "Reset Progress"
                will clear all guessed items and stop the timer (your attempt
                will NOT be saved)
              </li>
              <li>
                <strong>Switching Back:</strong> Going from Timed to Normal also
                resets progress
              </li>
              <li>
                <strong>Completing the List:</strong> Timer automatically stops
                and your time is saved only when you finish the entire list
              </li>
            </ul>
            <p className="text-red-400 mt-3">
              <strong>Why?</strong> This ensures fair tracking and prevents
              mixing timed and untimed attempts.
            </p>
          </div>
        </Section>

        <Section id="stats-tracking" title="Statistics Tracking">
          <p className="mb-4">
            Collect Arena tracks <strong>six statistics</strong> for each list
            you play:
          </p>

          <div className="space-y-3">
            <div className="bg-blue-500/10 border border-blue-500 rounded-lg p-4">
              <h4 className="text-blue-400 font-semibold mb-2">Best Time</h4>
              <p className="text-sm">
                Your fastest completion time in Timed mode
              </p>
            </div>

            <div className="bg-blue-500/10 border border-blue-500 rounded-lg p-4">
              <h4 className="text-blue-400 font-semibold mb-2">Last Time</h4>
              <p className="text-sm">
                Your most recent completion time in Timed mode
              </p>
            </div>

            <div className="bg-green-500/10 border border-green-500 rounded-lg p-4">
              <h4 className="text-green-400 font-semibold mb-2">
                Best (Normal)
              </h4>
              <p className="text-sm">
                Fewest total guesses in Normal mode (any difficulty)
              </p>
            </div>

            <div className="bg-green-500/10 border border-green-500 rounded-lg p-4">
              <h4 className="text-green-400 font-semibold mb-2">
                Last (Normal)
              </h4>
              <p className="text-sm">
                Your most recent guess count in Normal mode
              </p>
            </div>

            <div className="bg-red-500/10 border border-red-500 rounded-lg p-4">
              <h4 className="text-red-400 font-semibold mb-2">Best (Timed)</h4>
              <p className="text-sm">Fewest total guesses in Timed mode</p>
            </div>

            <div className="bg-red-500/10 border border-red-500 rounded-lg p-4">
              <h4 className="text-red-400 font-semibold mb-2">Last (Timed)</h4>
              <p className="text-sm">
                Your most recent guess count in Timed mode
              </p>
            </div>
          </div>

          <div className="bg-purple-500/10 border border-purple-500 rounded-lg p-4 mt-4">
            <p className="text-purple-400">
              <strong>Important:</strong> Stats only update when you{" "}
              <strong>complete</strong> a list. Partial progress or resets won't
              save your attempt!
            </p>
          </div>

          <div className="bg-yellow-500/10 border border-yellow-500 rounded-lg p-4 mt-4">
            <p className="text-yellow-400">
              <strong>Note:</strong> You <strong>cannot reset</strong> your
              personal bests or statistics. They are permanent records of your
              best performances!
            </p>
          </div>
        </Section>

        <Section id="progress-tracking" title="Progress & Saving">
          <div>
            <h3 className="text-xl font-semibold text-white mb-3">
              Automatic Saving
            </h3>
            <p className="mb-3">Your progress is saved automatically:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Every correct guess is saved instantly to the server</li>
              <li>Your current mode settings are remembered</li>
              <li>
                In Timed mode, timer progress persists even if you close the tab
                or browser
              </li>
              <li>Works across all your devices when logged in</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-white mb-3">
              Progress Display
            </h3>
            <p className="mb-3">On each list, you can see:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>
                <strong>Completion percentage:</strong> How many items you've
                guessed out of the total
              </li>
              <li>
                <strong>Total guesses:</strong> How many attempts you've made
                (includes both correct and incorrect guesses)
              </li>
              <li>
                <strong>Timer:</strong> Current elapsed time (in Timed mode)
              </li>
              <li>
                <strong>Statistics:</strong> Your best and last performances
              </li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-white mb-3">
              Resetting Progress
            </h3>
            <p className="mb-3">
              You can reset your progress using the "Reset Progress" button:
            </p>
            <div className="bg-dark-bg border border-gray-600 rounded p-4">
              <p className="text-white font-semibold mb-2">
                What happens when you reset:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>All guessed items are cleared</li>
                <li>Timer is stopped (in Timed mode)</li>
                <li>
                  Your current attempt is <strong>NOT saved</strong> to
                  statistics
                </li>
                <li>You start fresh with a clean slate</li>
                <li>
                  Your previous best and last stats remain unchanged (they
                  cannot be reset)
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-red-500/10 border border-red-500 rounded-lg p-4">
            <p className="text-red-400">
              <strong>Account Required:</strong> You must be logged in to play.
              Guest access is not available. All your progress is automatically
              synced across devices!
            </p>
          </div>
        </Section>

        <Section id="tips-tricks" title="Tips & Tricks">
          <div className="space-y-4">
            <div className="bg-dark-bg border border-gray-600 rounded p-4">
              <h4 className="text-white font-semibold mb-2">Strategy Tips</h4>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Start with the most obvious answers to build confidence</li>
                <li>
                  In Relaxed mode, don't worry about perfect spelling - close is
                  good enough!
                </li>
                <li>
                  In Timed mode, prioritize speed - you can always try again to
                  beat your best time
                </li>
                <li>
                  Use categories as hints - they often give clues about what to
                  expect
                </li>
                <li>
                  Read the list description carefully - it may contain helpful
                  hints
                </li>
              </ul>
            </div>

            <div className="bg-dark-bg border border-gray-600 rounded p-4">
              <h4 className="text-white font-semibold mb-2">
                Performance Tips
              </h4>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  Try Normal mode first to learn all the items, then switch to
                  Timed for speed
                </li>
                <li>
                  Practice in Relaxed difficulty before attempting Casual for
                  better accuracy
                </li>
                <li>
                  Reset your progress to practice repeatedly and improve your
                  stats
                </li>
                <li>
                  Track your improvement by comparing "Last" stats to "Best"
                  stats
                </li>
              </ul>
            </div>

            <div className="bg-dark-bg border border-gray-600 rounded p-4">
              <h4 className="text-white font-semibold mb-2">Good to Know</h4>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  Ranked lists show items in order when revealed (look for the
                  "Ranked" badge)
                </li>
                <li>
                  You can view all your progress across lists on the "My
                  Progress" page
                </li>
                <li>
                  Use the search bar on the homepage to find specific topics
                  quickly
                </li>
                <li>
                  Timer continues running in Timed mode even if you switch tabs
                  - it only pauses when you leave the page entirely
                </li>
              </ul>
            </div>
          </div>
        </Section>

        <Section id="faq" title="Frequently Asked Questions">
          <div className="space-y-4">
            <div>
              <h4 className="text-white font-semibold mb-2">
                Q: Do I need an account to play?
              </h4>
              <p>
                <strong>Yes!</strong> You must create a free account to play
                Collect Arena. Guest access is not available. This allows us to
                save your progress and sync it across all your devices.
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-2">
                Q: Why did my progress reset when I changed modes?
              </h4>
              <p>
                Switching between Normal and Timed modes resets progress to
                ensure fair statistics tracking and prevents mixing timed and
                untimed attempts. You'll always see a warning before this
                happens.
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-2">
                Q: Can I switch between Casual and Relaxed without losing
                progress?
              </h4>
              <p>
                Yes! Switching difficulty modes (Casual ↔ Relaxed) while staying
                in the same game mode (Normal or Timed) preserves your progress.
                This is only possible in normal mode! Timed mode is always on
                relaxed difficulty.
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-2">
                Q: How does the timer work?
              </h4>
              <p>
                The timer starts once you select Timed mode. It pauses and
                remembers your exact time when you leave the page or close the
                tab. When you return, it resumes from where you left off. The
                timer only stops permanently when you complete the list or reset
                your progress.
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-2">
                Q: What counts as a "guess"?
              </h4>
              <p>
                Every time you submit an answer (whether correct or incorrect),
                it counts as a guess. This total is tracked separately from
                correct answers in your statistics.
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-2">
                Q: Can I reset my personal best statistics?
              </h4>
              <p>
                <strong>No.</strong> Your best and last statistics are permanent
                records and cannot be reset. This ensures fair competition and
                accurate tracking of your improvement over time.
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-2">
                Q: Can I see which items I haven't guessed yet?
              </h4>
              <p>
                No, that would defeat the purpose of the quiz! But you can see
                your completion percentage to know how close you are to
                finishing.
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-2">
                Q: What happens if I reset my progress in Timed mode?
              </h4>
              <p>
                If you reset your progress, the timer stops and your current
                attempt is <strong>NOT saved</strong> to your statistics. You'll
                start completely fresh. Only completed lists count toward your
                stats!
              </p>
            </div>
          </div>
        </Section>

        <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-lg p-6 mt-8">
          <h2 className="text-2xl font-semibold text-white mb-3">
            Ready to Test Your Knowledge?
          </h2>
          <p className="text-gray-300 mb-4">
            Now that you know how everything works, it's time to put your
            knowledge to the test! Start with a category you're confident in,
            then explore new topics.
          </p>
          <div className="flex gap-4">
            <Link
              to="/"
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition"
            >
              Browse All Lists
            </Link>
            <Link
              to="/about"
              className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition"
            >
              Learn More About Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowToPlay;
